import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Clock, TrendingUp, Target, Award, BookOpen } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

const CHART_COLORS = ['#2D5F3F', '#4CAF50', '#FFC107', '#E57373'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-popover border border-border rounded-xl px-3 py-2 shadow-xl text-sm">
        <p className="font-medium">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="font-bold">{p.name}: {p.value}</p>
        ))}
      </div>
    );
  }
  return null;
};

export default function StatsPage() {
  const [stats, setStats] = useState(null);
  const [weeklyData, setWeeklyData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, weeklyRes] = await Promise.all([
        api.get('/stats'),
        api.get('/stats/weekly')
      ]);
      setStats(statsRes.data);
      setWeeklyData(weeklyRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-48 bg-muted rounded-2xl animate-pulse" />)}</div>;
  }

  const pieData = [
    { name: 'Hábitos', value: stats?.total_habits_completed || 0 },
    { name: 'Dias mantendo o plano', value: stats?.total_clean_days || 0 },
    { name: 'Diário', value: stats?.journal_count || 0 },
  ].filter(d => d.value > 0);

  const metricCards = [
    { label: 'Você economizou', value: `R$ ${(stats?.total_savings || 0).toFixed(2)}`, icon: DollarSign, color: 'text-emerald-500' },
    { label: 'Horas recuperadas', value: `${(stats?.hours_recovered || 0).toFixed(0)}h`, icon: Clock, color: 'text-blue-500' },
    { label: 'Total hábitos concluídos', value: stats?.total_habits_completed || 0, icon: Target, color: 'text-primary' },
    { label: 'XP Total', value: stats?.xp || 0, icon: TrendingUp, color: 'text-primary' },
    { label: 'Nível', value: stats?.level || 'Iniciante', icon: Award, color: 'text-yellow-500' },
    { label: 'Entradas diário', value: stats?.journal_count || 0, icon: BookOpen, color: 'text-purple-500' },
  ];

  return (
    <div className="space-y-6" data-testid="stats-page">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Estatísticas</h1>
        <p className="text-muted-foreground text-sm mt-1">Sua visão geral de progresso financeiro, de tempo e de rotina</p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 stagger-children" data-testid="metric-cards">
        {metricCards.map((m, i) => {
          const Icon = m.icon;
          return (
            <Card key={i} className="hover:shadow-md transition-shadow">
              <CardContent className="py-4 px-4">
                <Icon className={`w-5 h-5 ${m.color} mb-2`} />
                <p className="text-2xl font-heading font-bold">{m.value}</p>
                <p className="text-xs text-muted-foreground">{m.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Weekly Bar Chart */}
      <Card data-testid="weekly-bar-chart">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Hábitos por dia</CardTitle>
        </CardHeader>
        <CardContent>
          {weeklyData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Comece marcando seus hábitos para ver este gráfico ganhar vida.
            </p>
          ) : (
            <div style={{ width: '100%', height: 224 }}>
              <ResponsiveContainer width="100%" height={224}>
                <BarChart data={weeklyData} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Hábitos" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Progress Line Chart */}
      <Card data-testid="progress-line-chart">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Progresso ao longo do tempo</CardTitle>
        </CardHeader>
        <CardContent>
          {weeklyData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Assim que você tiver alguns dias registrados, mostraremos sua evolução aqui.
            </p>
          ) : (
            <div style={{ width: '100%', height: 224 }}>
              <ResponsiveContainer width="100%" height={224}>
                <LineChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="count" name="Hábitos" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4, fill: 'hsl(var(--primary))' }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pie Chart */}
      {pieData.length > 0 && (
        <Card data-testid="distribution-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Distribuição de atividades</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 224 }}>
              <ResponsiveContainer width="100%" height={224}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                    {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
